import { IContract } from '@components/DeviceActionModal/types';
import SessionContext from '@providers/Session/SessionContext';
import rolesConstants from '@utils/rolesConstants';
import {
  getStatusInventoryMoTOptions,
  getStatusInventoryOptions,
} from '@utils/status';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from './useSwr';

interface InventoryFiltersProps {
  showContracted: boolean;
  showSelectCustomer: boolean;
  isEndCustomer: boolean;
  isBroker: boolean;
}

export const useInventoryFilters = ({
  showContracted,
  showSelectCustomer,
  isEndCustomer,
  isBroker,
}: InventoryFiltersProps) => {
  const { t } = useTranslation(['inventory', 'device-actions-modal']);
  const { t: tStatus } = useTranslation('status');

  const {
    hasPermission,
    hasCustomersRoles,
    isNotChildOfRoot,
    isMot,
    isRoot,
    isMotPay,
    isMotBusiness,
    isMotEnterprise,
    selectedApp,
  } = useContext(SessionContext);

  const { data: contractsData } = useSwr<IContract[] | ''>(
    !isMot ? '/service-proxy/contracts' : null,
    {
      params: {
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contractsData)
        ? contractsData?.map(({ assetNumber, planName, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
          }))
        : [],
    [contractsData],
  );
  // const specificOperators = useMemo(
  //   () =>
  //     operatorsData?.map(({ name }: { name: string }) => ({
  //       label: name,
  //       value: name,
  //     })),
  //   [operatorsData],
  // );

  const originalOperatorsOptions = [
    'ALGAR',
    'ARQIA',
    'CLARO',
    'TIM',
    'VIVO',
  ].map(opName => ({
    label: opName,
    value: opName,
  }));

  const statusOptions = useMemo(
    () =>
      isMot
        ? getStatusInventoryMoTOptions(tStatus)
        : getStatusInventoryOptions(tStatus),
    [tStatus, isMot],
  );

  const getLastAlert = useMemo(() => {
    return [
      {
        label: t('attributes:lastAlertDateFrom.lastOneHour'),
        value: moment().subtract(1, 'hours').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastThreeHours'),
        value: moment().subtract(3, 'hours').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastSixHours'),
        value: moment().subtract(6, 'hours').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastTwelveHours'),
        value: moment().subtract(12, 'hours').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastDay'),
        value: moment().subtract(24, 'hours').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastWeek'),
        value: moment().subtract(7, 'days').format(),
      },
      {
        label: t('attributes:lastAlertDateFrom.lastMonth'),
        value: moment().subtract(1, 'months').format(),
      },
    ];
  }, [t]);

  const networkBlockStatusOptions = useMemo(() => {
    const defaultOptions = [
      {
        label: t('lock-status.types.NONE'),
        value: 'NONE',
      },
      {
        label: t('lock-status.types.PERMANENT'),
        value: 'PERMANENT',
      },
      {
        label: t('lock-status.types.TEMPORARY'),
        value: 'TEMPORARY',
      },
      {
        label: t('lock-status.types.UNTIL_RENEWAL'),
        value: 'UNTIL_RENEWAL',
      },
      {
        label: t('lock-status.types.FINANCIAL'),
        value: 'FINANCIAL',
      },
    ];

    if (isMot) {
      defaultOptions.push(
        {
          label: t('lock-status.types.temporary-suspension'),
          value: 'SUSPENSION',
        },
        {
          label: t('lock-status.types.CANCELLATION'),
          value: 'CANCELLATION',
        },
        {
          label: t('lock-status.types.QUARANTINE'),
          value: 'QUARANTINE',
        },
        {
          label: t('lock-status.types.SUBSTITUTION'),
          value: 'SUBSTITUTION',
        },
      );
    } else {
      defaultOptions.push({
        label: t('lock-status.types.SUSPENSION'),
        value: 'SUSPENSION',
      });
    }

    return defaultOptions;
  }, [isMot, t]);

  const filtersMot: any = [
    ...((!showContracted && !isNotChildOfRoot && !isRoot) ||
    (!isRoot && isNotChildOfRoot && showContracted)
      ? [
          {
            name: t('table.lastAlertDateFrom'),
            value: 'lastAlertDateFrom',
            rules: ['Date'],
            options: getLastAlert,
            visible: hasPermission(
              rolesConstants.INVENT_LAST_ALERT_DATE,
              'inventory',
            ),
            category: 'date',
          },
          {
            name: t('table.automaticBlocking'),
            value: 'automaticBlocking',
            rules: ['Options'],
            options: [
              {
                label: t('options.autoBlock.block'),
                value: 'true',
              },
              {
                label: t('options.autoBlock.unblock'),
                value: 'false',
              },
            ],
            visible: hasPermission(
              rolesConstants.INVENT_AUTOMATIC_BLOCKING,
              'inventory',
            ),
            category: 'contract',
          },
          {
            name: t('table.preActivationEndDateFrom'),
            value: 'preActivationEndDateFrom',
            rules: ['DateRange'],
            date: {
              start: 'preActivationEndDateFrom',
              end: 'preActivationEndDateTo',
            },
            visible: hasPermission(
              rolesConstants.INVENT_PRE_ACTIVATION_END_DATE,
              'inventory',
            ),
            category: 'date',
          },
          {
            name: t('table.franchising'),
            value: 'franchise',
            rules: ['Franchise'],
            visible:
              !isRoot &&
              hasPermission(rolesConstants.INVENT_FRANCHISE, 'inventory'),
            category: 'details',
          },
        ]
      : []),

    ...((!showContracted && isRoot) || (!isNotChildOfRoot && showContracted)
      ? []
      : [
          {
            name: t('table.price'),
            value: 'priceFrom',
            rules: ['RangeValues', 'R$'],
            range: {
              from: 'priceFrom',
              to: 'priceTo',
            },
            visible: hasPermission(rolesConstants.INVENT_PRICE, 'inventory'),
            category: 'range',
          },
          {
            name: t('table.consumptionPercentTo'),
            value: 'consumptionPercent',
            rules: ['RangeValues', '%'],
            range: {
              from: 'consumptionPercentFrom',
              to: 'consumptionPercentTo',
            },
            visible:
              !isRoot &&
              hasPermission(rolesConstants.INVENT_CONSUMPTION, 'inventory'),
            category: 'range',
          },
        ]),

    {
      name: t('table.planType'),
      value: 'planTypes',
      rules: ['Options'],
      options: [
        {
          label: t('attributes:planTypes.bait'),
          value: 'BAIT',
        },
        {
          label: t('attributes:planTypes.standard'),
          value: 'STANDARD',
        },
      ],
      visible:
        isMotEnterprise &&
        ((!showContracted && isBroker) || (isEndCustomer && showContracted)) &&
        hasPermission(rolesConstants.INVENT_PLANTYPE, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.hostname'),
      value: 'hostname',
      rules: ['Equals'],
      visible: hasPermission(rolesConstants.INVENT_HOSTNAME, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.operator'),
      value: 'operator',
      rules: ['Options'],
      options: originalOperatorsOptions ?? [],
      visible: hasPermission(rolesConstants.INVENT_OPERATOR, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.deviceNumber'),
      value: 'deviceNumber',
      rules: ['Equals'],
      visible:
        hasPermission(rolesConstants.INVENT_DEVICE_NUMBER, 'inventory') &&
        isMotPay,
      category: 'details',
    },
    {
      name: t('table.cancellationDate'),
      value: 'cancellation',
      rules: ['DateRange'],
      visible: hasPermission(
        rolesConstants.INVENT_CANCELLATION_DATE,
        'inventory',
      ),
      date: {
        start: 'cancellationDateFrom',
        end: 'cancellationDateTo',
      },
      category: 'date',
    },

    ...(isMotBusiness
      ? [
          ...(!isEndCustomer
            ? [
                {
                  name: t('table.brokerContractDesc'),
                  value: 'brokerContractId',
                  rules: ['Options'],
                  options: [],
                  visible: hasPermission(
                    rolesConstants.INVENT_BROKER_CONTRACT_ID,
                    'inventory',
                  ),
                  category: 'details',
                },
              ]
            : []),
          {
            name: t('table.groupings'),
            value: 'accountId',
            rules: ['Options'],
            options: [],
            visible: hasPermission(
              rolesConstants.INVENT_ACCOUNT_ID,
              'inventory',
            ),
            category: 'details',
          },
        ]
      : [
          {
            name: t('table.accountId'),
            value: 'accountId',
            rules: ['Equals'],
            visible: hasPermission(
              rolesConstants.INVENT_ACCOUNT_ID,
              'inventory',
            ),
            category: 'contract',
          },
        ]),
  ];

  const filtersMultiConnect = [
    {
      name: t('table.hostname'),
      value: 'hostName',
      rules: ['Equals'],
      visible: hasPermission(rolesConstants.INVENT_HOSTNAME, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.planType'),
      value: 'planTypes',
      rules: ['Options'],
      options: [
        {
          label: t('attributes:planTypes.bait'),
          value: 'BAIT',
        },
        {
          label: t('attributes:planTypes.standard'),
          value: 'STANDARD',
        },
      ],
      visible: hasPermission(rolesConstants.INVENT_PLANTYPE, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.consumptionPercentTo'),
      value: 'consumptionPercent',
      rules: ['RangeValues', '%'],
      range: {
        from: 'consumptionPercentFrom',
        to: 'consumptionPercentTo',
      },
      visible: hasPermission(rolesConstants.INVENT_CONSUMPTION, 'inventory'),
      category: 'range',
    },
    {
      name: t('table.plan'),
      value: 'plan',
      rules: ['Equals'],
      visible: hasPermission(rolesConstants.INVENT_PLAN, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.franchising'),
      value: 'franchise',
      rules: ['Franchise'],
      visible: hasPermission(rolesConstants.INVENT_FRANCHISE, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.isca'),
      value: 'isca',
      rules: ['Options'],
      options: [
        {
          label: t('iscaOptions.no'),
          value: 'false',
        },
        {
          label: t('iscaOptions.yes'),
          value: 'true',
        },
      ],
      visible: hasPermission(rolesConstants.INVENT_ISCA, 'inventory'),
      category: 'details',
    },
    {
      name: t('table.contractAssetNumber'),
      value: 'contractAssetNumber',
      rules: ['Options'],
      options: contractsOptions,
      visible: hasPermission(
        rolesConstants.INVENT_CONTRACT_ASSET_NUMBER,
        'inventory',
      ),
      category: 'details',
    },
  ];

  const commonFilters = useMemo(
    () => [
      {
        name: isMotBusiness ? t('table.department') : t('table.client'),
        value: 'customerId',
        rules: ['Client'],
        visible:
          !showContracted &&
          hasCustomersRoles &&
          hasPermission(rolesConstants.INVENT_CUSTOMER_NAME, 'inventory') &&
          showSelectCustomer,
        category: 'details',
      },
      {
        name: 'MSISDN',
        value: 'msisdn',
        rules: ['Equals'],
        visible: hasPermission(rolesConstants.INVENT_MSISDN, 'inventory'),
        category: 'details',
      },
      {
        name: 'IMSI',
        value: 'imsi',
        rules: ['Equals'],
        visible: hasPermission(rolesConstants.INVENT_IMSI, 'inventory'),
        category: 'details',
      },
      {
        name: 'IMEI',
        value: 'imei',
        rules: ['Equals'],
        visible: hasPermission(rolesConstants.INVENT_IMEI, 'inventory'),
        category: 'details',
      },
      {
        name: 'ICCID',
        value: 'iccid',
        rules: ['Equals'],
        visible: hasPermission(rolesConstants.INVENT_ICCID, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.network-status'),
        value: 'online',
        rules: ['Options'],
        options: [
          {
            label: 'Online',
            value: 'true',
          },
          {
            label: 'Offline',
            value: 'false',
          },
        ],
        visible: hasPermission(rolesConstants.INVENT_ONLINE, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.connected-operator'),
        value: 'connectedOperator',
        rules: ['Options'],
        options: originalOperatorsOptions ?? [],
        visible: hasPermission(
          rolesConstants.INVENT_CONNECTED_OPERATOR,
          'inventory',
        ),
        category: 'details',
      },
      {
        name: t('table.status'),
        value: 'status',
        rules: ['Options'],
        options: statusOptions,
        visible: hasPermission(rolesConstants.INVENT_STATUS, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.nickname'),
        value: 'nickname',
        rules: ['Equals'],
        visible: hasPermission(rolesConstants.INVENT_NICKNAME, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.technology'),
        value: 'technology',
        rules: ['Options'],
        options: [
          {
            label: '2G',
            value: '2G',
          },
          {
            label: '3G',
            value: '3G',
          },
          {
            label: '4G',
            value: '4G',
          },
          {
            label: '5G',
            value: '5G',
          },
          {
            label: 'NB-IoT',
            value: 'NB-IoT',
          },
          {
            label: 'CAT-M',
            value: 'CAT-M',
          },
        ],
        visible: hasPermission(rolesConstants.INVENT_FRANCHISE, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.lastApnConnected'),
        value: 'apn',
        rules: ['Options'],
        visible: hasPermission(rolesConstants.INVENT_APN, 'inventory'),
        category: 'details',
      },
      {
        name: t('table.provisionedApn'),
        value: 'provisionedApn',
        rules: ['Options'],
        visible: hasPermission(
          rolesConstants.INVENT_APN_PROVISIONED,
          'inventory',
        ),
        category: 'details',
      },
      {
        name: t('table.last-connection'),
        value: 'lastConnectionFrom',
        rules: ['DateRange'],
        visible: hasPermission(
          rolesConstants.INVENT_LAST_CONNECTION,
          'inventory',
        ),
        date: {
          start: 'lastConnectionFrom',
          end: 'lastConnectionTo',
        },
        category: 'date',
      },
      {
        name: t('table.activation-date'),
        value: 'activationDateFrom',
        rules: ['DateRange'],
        visible: hasPermission(
          rolesConstants.INVENT_ACTIVATION_DATE,
          'inventory',
        ),
        date: {
          start: 'activationDateFrom',
          end: 'activationDateTo',
        },
        category: 'date',
      },
      {
        name: t('table.imeiLocked'),
        value: 'imeiLocked',
        rules: ['Options'],
        options: [
          {
            label: t('options.imeiLocked.unlock'),
            value: 'false',
          },
          {
            label: t('options.imeiLocked.lock'),
            value: 'true',
          },
        ],
        visible: hasPermission(rolesConstants.INVENT_IMEI_LOCKED, 'inventory'),
        category: 'contract',
      },
      {
        name: t('table.networkBlockStatus'),
        value: 'networkBlockStatus',
        rules: ['Options'],
        options: networkBlockStatusOptions,
        visible: hasPermission(
          rolesConstants.INVENT_NETWORK_BLOCK_STATUS,
          'inventory',
        ),
        category: 'contract',
      },
    ],
    [
      isMotBusiness,
      t,
      showContracted,
      hasCustomersRoles,
      hasPermission,
      showSelectCustomer,
      originalOperatorsOptions,
      statusOptions,
      networkBlockStatusOptions,
    ],
  );

  const filtersData = isMot
    ? [...commonFilters, ...filtersMot]
    : [...commonFilters, ...filtersMultiConnect];

  return {
    filtersData,
  };
};
